import React from 'react'
import { useDrag } from 'react-dnd'

import { pxToRem } from '@/shared/styles/themes'
import { faArrowUpRightFromSquare, faCoffee, faDownload, faFlag, faFlagCheckered, faLocationDot, faUpload } from '@fortawesome/pro-solid-svg-icons'
import { Grid, styled, Typography } from '@mui/material'

import { CustomTooltip } from '../CustomTooltip/CustomTooltip'
import { Icon } from '../Icon/Icon'
import { IconButton } from '../IconButton/IconButton'
import { useGanttContext } from './GanttContext'
import { getPositionFromTime } from './libs/getPositionFromTime'
import { GanttChartDragType } from './models/GanttChartDragType'
import { TimelineStop } from './models/GanttRow'
import { StyledTooltipPaper } from './StyledTooltip'

export type GanttStopProps = {
  color: string
  stop: TimelineStop
  startHour: number
  baseWidth: number
}

export const GanttStop: React.FC<GanttStopProps> = ({ color, stop, startHour, baseWidth }) => {
  const { zoom } = useGanttContext()

  // Convert duration (minutes) to hours and calculate width
  const getDurationWidth = (duration: number): number => {
    return (duration / 60) * (baseWidth * zoom)
  }

  const getStopTypeIcon = (stopType: string, primary: boolean) => {
    if (stopType === 'Start') {
      return <StyledIcon icon={faFlag} $primary={primary} />
    } else if (stopType === 'Finish') {
      return <StyledIcon icon={faFlagCheckered} $primary={primary} />
    } else if (stopType === 'Break') {
      return <StyledIcon icon={faCoffee} $primary={primary} />
    } else if (stopType === 'Location') {
      return <StyledIcon icon={faLocationDot} $primary={primary} />
    } else if (stopType === 'Pickup') {
      return <StyledIcon icon={faUpload} $primary={primary} />
    } else if (stopType === 'Delivery') {
      return <StyledIcon icon={faDownload} $primary={primary} />
    }

    return null
  }

  const [{ isDragging }, dragRef] = useDrag(() => ({
    type: GanttChartDragType.STOP,
    item: stop.isDraggable ? {
      id: stop.id,
      type: GanttChartDragType.STOP,
      stop: stop,
    } : null,
    canDrag: stop.isDraggable,
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  }), [stop])

  const tooltipContent = (
    <StyledTooltipPaper>
      <Grid container justifyContent={'space-between'} alignItems={'center'}>
        <Grid item>
          <Typography variant="body1" sx={{ fontWeight: 500 }}>
            Stop No. {stop.label}
          </Typography>
        </Grid>
        <Grid item>
          {stop.action && (
            <IconButton onClick={stop.action} sx={{ margin: 0 }}>
              <Icon icon={faArrowUpRightFromSquare} sx={{ fontSize: pxToRem(16) }} />
            </IconButton>
          )}
        </Grid>
      </Grid>
      <Grid container justifyContent={'space-between'} alignItems={'center'}>
        <Grid item>
          <Typography variant="body1">
            Stop Type
          </Typography>
        </Grid>
        <Grid item sx={{ display: 'inline-flex', gap: '2px' }}>
          {getStopTypeIcon(stop.displayStopType, true)}
          <Typography variant="body2">
            {stop.displayStopType}
          </Typography>
        </Grid>
      </Grid>
      {stop.additionalInfo && (
        <>
          {stop.additionalInfo.map((info) => (
            <Grid key={info.key} container alignItems={'space-between'} spacing={1} wrap="nowrap" sx={{ minWidth: 0 }}>
              <Grid item sx={{ flexShrink: 0, marginRight: '4px' }}>
                <Typography variant="body1" noWrap>
                  {info.key}
                </Typography>
              </Grid>
              <Grid item sx={{ flexGrow: 1, minWidth: 0, textAlign: 'right' }}>
                <Typography variant="body2" sx={{ wordBreak: 'break-word', textAlign: 'right' }}>
                  {info.value}
                </Typography>
              </Grid>
            </Grid>
          ))}
        </>
      )}
    </StyledTooltipPaper>
  )

  const stopContent = (
    <StyledStop
      key={stop.id}
      ref={dragRef}
      $left={getPositionFromTime({
        time: stop.startTime,
        startHour,
        zoom,
        baseWidth,
      })}
      $width={getDurationWidth(stop.duration)}
      $color={color}
      $stopType={stop.displayStopType}
      $isDraggable={stop.isDraggable}
      $isDragging={isDragging}
    >
      {!['Start', 'Finish'].includes(stop.displayStopType) && <>
        <StyledStopCircle>
          <StyledStopLabel>
            {stop.label}
          </StyledStopLabel>
        </StyledStopCircle>
        {getStopTypeIcon(stop.displayStopType, false)}
      </>}
    </StyledStop>
  )

  return (
    <CustomTooltip
      title={isDragging ? '' : tooltipContent}
      placement="bottom-start"
      disableInteractive={false}
    >
      {stopContent}
    </CustomTooltip>
  )
}

const StyledStop = styled('div', {
  shouldForwardProp: (prop) => prop !== '$left' && prop !== '$width' && prop !== '$color' && prop !== '$stopType' && prop !== '$isDragging' && prop !== '$isDraggable',
}) <{
  $left: number
  $width: number
  $color: string
  $stopType: string
  $isDragging?: boolean
  $isDraggable?: boolean
}>`
  position: absolute;
  top: ${pxToRem(2)};
  left: ${props => props.$left}px;
  width: ${props => props.$width}px;
  height: ${pxToRem(32)};
  z-index: 1;
  background-color: ${props => props.$color};
  border-radius: ${pxToRem(4)};
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: ${props => props.theme.palette.common.white};
  font-size: ${pxToRem(14)};
  overflow: hidden;
  min-width: 4px;
  opacity: ${props => props.$isDragging ? 0.3 : 1};
  cursor: ${props => props.$isDraggable ? 'pointer' : 'default'};

  ${props => ['Start', 'Finish'].includes(props.$stopType) && `
    background-color: ${props.theme.palette.text.primary};
  `}

  ${props => props.$stopType === 'Break' && `
    border-radius: ${pxToRem(16)};
  `}
`

const StyledStopCircle = styled('div')`
  min-width: ${pxToRem(24)};
  min-height: ${pxToRem(24)};
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  margin-left: ${pxToRem(2)};
  margin-right: ${pxToRem(12)};
  background-color: ${props => props.theme.palette.background.paper};
`

const StyledStopLabel = styled(Typography)`
  font-size: ${pxToRem(13)};
  color: ${props => props.theme.palette.text.primary};
  font-weight: 500;
`

const StyledIcon = styled(Icon, { shouldForwardProp: prop => prop !== '$primary' }) <{ $primary: boolean }>`
  font-size: 16px;
  margin-right: 5px;
  color: ${props => props.$primary ? props.theme.palette.text.primary : props.theme.palette.background.paper};
`
